import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ContactColumn.css';
import { FaTrash, FaEdit, FaSave, FaTimes } from 'react-icons/fa';
import { MdOutlineModeEdit } from "react-icons/md";

const ContactColumn = ({ currentUser }) => {
  const [columns, setColumns] = useState({});
  const [newLabel, setNewLabel] = useState('');
  const [newType, setNewType] = useState('text');
  const [newRequired, setNewRequired] = useState(false);
  const [newShowOnForm, setNewShowOnForm] = useState(true);
  const [newOptions, setNewOptions] = useState([]);
  const [optionInput, setOptionInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterRequired, setFilterRequired] = useState('');
  const [filterShow, setFilterShow] = useState('');

  const [editKey, setEditKey] = useState(null);
  const [editedColumn, setEditedColumn] = useState({});
  const [editedOptions, setEditedOptions] = useState([]);

  const defaultFields = ['name', 'phoneNumber', 'email', 'keys'];

  useEffect(() => {
    fetchColumns();
  }, []);

  const fetchColumns = async () => {
    try {
      const response = await axios.post('https://gambot.azurewebsites.net/api/Webhooks/GetDynamicContactColumns', {
        organization: currentUser.organization
      });
      setColumns(response.data || {});
    } catch (error) {
      console.error('Error fetching contact columns:', error);
    }
  };

  const generateKeyFromLabel = (label) => {
    return label
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0590-\u05FF]/g, '')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '');
  };

  const handleAddField = () => {
    const key = generateKeyFromLabel(newLabel);
    if (!key || !newLabel.trim()) return;

    const updatedColumns = {
      ...columns,
      [key]: {
        label: newLabel,
        type: newType,
        required: newRequired,
        showOnForm: newShowOnForm,
        ...(newType === 'select' && newOptions.length > 0 ? { options: newOptions } : {})
      }
    };

    setColumns(updatedColumns);
    setNewLabel('');
    setNewType('text');
    setNewRequired(false);
    setNewShowOnForm(true);
    setNewOptions([]);
    setOptionInput('');
  };

  const handleDeleteField = (keyToDelete) => {
    const { [keyToDelete]: _, ...rest } = columns;
    setColumns(rest);
  };

  const handleSaveAll = async () => {
    setIsLoading(true);
    try {
      await axios.post('https://gambot.azurewebsites.net/api/Webhooks/SaveContactsColumnsByOrg', {
        organization: currentUser.organization,
        columns
      });
      alert('Contact columns updated successfully');
    } catch (error) {
      console.error('Error saving contact columns:', error);
      alert('Failed to save contact columns');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredEntries = Object.entries(columns).filter(([key, col]) => {
    if (defaultFields.includes(key)) return false;
    const matchesSearch = col.label.toLowerCase().includes(search.toLowerCase());
    const matchesType = filterType ? col.type === filterType : true;
    const matchesRequired = filterRequired !== '' ? String(col.required) === filterRequired : true;
    const matchesShow = filterShow !== '' ? String(col.showOnForm) === filterShow : true;
    return matchesSearch && matchesType && matchesRequired && matchesShow;
  });

  const startEdit = (key, column) => {
    setEditKey(key);
    setEditedColumn({ ...column });
    setEditedOptions(column.options || []);
  };

  const cancelEdit = () => {
    setEditKey(null);
    setEditedColumn({});
    setEditedOptions([]);
  };

  const saveEdit = (key) => {
    setColumns({
      ...columns,
      [key]: {
        ...editedColumn,
        ...(editedColumn.type === 'select' ? { options: editedOptions } : {})
      }
    });
    cancelEdit();
  };

  const updateEditedField = (field, value) => {
    setEditedColumn({ ...editedColumn, [field]: value });
  };

  const handleAddEditedOption = (value) => {
    const trimmed = value.trim();
    if (trimmed && !editedOptions.includes(trimmed)) {
      setEditedOptions([...editedOptions, trimmed]);
    }
  };

  const handleRemoveEditedOption = (value) => {
    setEditedOptions(editedOptions.filter(opt => opt !== value));
  };

  return (
    <div className="contact-column-container">
      <h2 className="contact-column-title">Manage Contact Columns</h2>

      <div className="contact-column-input-group">
        <input
          className="contact-column-input"
          placeholder="Label (e.g. Birthday)"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
        />
        <select
          className="contact-column-select"
          value={newType}
          onChange={(e) => setNewType(e.target.value)}
        >
          <option value="text">Text</option>
          <option value="number">Number</option>
          <option value="date">Date</option>
          <option value="boolean">Yes/No</option>
          <option value="select">Dropdown</option>
        </select>
        <label className="contact-column-checkbox">
          <input
            type="checkbox"
            checked={newRequired}
            onChange={(e) => setNewRequired(e.target.checked)}
          /> Required
        </label>
        <label className="contact-column-checkbox">
          <input
            type="checkbox"
            checked={newShowOnForm}
            onChange={(e) => setNewShowOnForm(e.target.checked)}
          /> Show on Form
        </label>
        <button className="contact-column-add-button" onClick={handleAddField}>Add Field</button>
      </div>

      <div className="contact-column-filters">
        <input
          className="contact-column-input"
          placeholder="Search by label..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="contact-column-select" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
          <option value="">All Types</option>
          <option value="text">Text</option>
          <option value="number">Number</option>
          <option value="date">Date</option>
          <option value="boolean">Yes/No</option>
          <option value="select">Dropdown</option>
        </select>
        <select className="contact-column-select" value={filterRequired} onChange={(e) => setFilterRequired(e.target.value)}>
          <option value="">Required?</option>
          <option value="true">✔️</option>
          <option value="false">❌</option>
        </select>
        <select className="contact-column-select" value={filterShow} onChange={(e) => setFilterShow(e.target.value)}>
          <option value="">Show on Form?</option>
          <option value="true">✔️</option>
          <option value="false">❌</option>
        </select>
      </div>

      <table className="contact-column-table">
        <thead>
          <tr>
            <th>Label</th>
            <th>Type</th>
            <th>Required</th>
            <th>Show on Form</th>
            <th>Options</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredEntries.map(([key, col]) => (
            <tr key={key}>
              {editKey === key ? (
                <>
                  <td>
                    <input
                      className="contact-column-input"
                      value={editedColumn.label}
                      onChange={(e) => updateEditedField('label', e.target.value)}
                    />
                  </td>
                  <td>
                    <select
                      value={editedColumn.type}
                      onChange={(e) => updateEditedField('type', e.target.value)}
                    >
                      <option value="text">Text</option>
                      <option value="number">Number</option>
                      <option value="date">Date</option>
                      <option value="boolean">Yes/No</option>
                      <option value="select">Dropdown</option>
                    </select>
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={editedColumn.required}
                      onChange={(e) => updateEditedField('required', e.target.checked)}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={editedColumn.showOnForm}
                      onChange={(e) => updateEditedField('showOnForm', e.target.checked)}
                    />
                  </td>
                  <td>
                    {editedColumn.type === 'select' ? (
                      <div>
                        {editedOptions.map((opt, i) => (
                          <div key={i}>
                            {opt} <button onClick={() => handleRemoveEditedOption(opt)}>x</button>
                          </div>
                        ))}
                        <input
                          placeholder="Add option"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddEditedOption(e.target.value);
                              e.target.value = '';
                            }
                          }}
                        />
                      </div>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    <button onClick={() => saveEdit(key)}><FaSave /></button>
                    <button onClick={cancelEdit}><FaTimes /></button>
                  </td>
                </>
              ) : (
                <>
                  <td>{col.label}</td>
                  <td>{col.type}</td>
                  <td>{col.required ? '✔️' : '❌'}</td>
                  <td>{col.showOnForm ? '✔️' : '❌'}</td>
                  <td>{col.options?.join(', ') || '-'}</td>
                  <td>
                    <MdOutlineModeEdit className='contact-column-edit' onClick={() => startEdit(key, col)}/>
                    <FaTrash className='contact-column-delete' onClick={() => handleDeleteField(key)} style={{ marginLeft: '10px', cursor: 'pointer' }} />
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      <button
        className="contact-column-save-button"
        onClick={handleSaveAll}
        disabled={isLoading}
      >
        Save All
      </button>
    </div>
  );
};

export default ContactColumn;
